import { useCallback, useState } from 'react';

import { DEFAULT_ENTITY_MAP_FILTER_STATE } from '@/components/map/utils/entityMapFilters';
import type { EntityMapFilterState } from '@/components/map/utils/entityMapFilters';
import {
    clearPersistedEntityMapFilters,
    loadPersistedEntityMapFilters,
    savePersistedEntityMapFilters,
} from '@/components/map/utils/entityMapFilterPersistence';
import type { EntityMapFilterScope } from '@/components/map/utils/entityMapFilterPersistence';

interface UseEntityMapFiltersOptions {
    /**
     * URL-derived filter state (the `filters=v2:` param). Non-null takes priority over
     * localStorage; null means no URL filter state, so the persisted restore applies.
     */
    initialFromUrl?: { isActive: boolean; committed: EntityMapFilterState } | null;
    /**
     * Whose filters these are. Each mode persists under its own key, so Find & Fix and
     * Network Explorer never see each other's committed state. Defaults to Find & Fix.
     */
    scope?: EntityMapFilterScope;
}

/**
 * The split-model Filters-panel state machine: a draft the panel edits, a committed state
 * the map/table/stats consume, and an explicit isActive flag — Apply commits and persists,
 * Reset deactivates and clears, closing the panel without Apply discards the draft.
 * Identical interaction model to the pre-split hook, over the entity filter state.
 */
export function useEntityMapFilters({ initialFromUrl, scope = 'find-and-fix' }: UseEntityMapFiltersOptions = {}) {
    const [{ initialIsActive, initialCommitted }] = useState<{
        initialIsActive: boolean;
        initialCommitted: EntityMapFilterState;
    }>(() => {
        if (initialFromUrl != null) {
            return { initialIsActive: initialFromUrl.isActive, initialCommitted: initialFromUrl.committed };
        }
        const persisted = loadPersistedEntityMapFilters(scope);
        if (persisted?.isActive) {
            return { initialIsActive: true, initialCommitted: persisted.committed };
        }
        return { initialIsActive: false, initialCommitted: DEFAULT_ENTITY_MAP_FILTER_STATE };
    });

    const [committed, setCommitted] = useState<EntityMapFilterState>(initialCommitted);
    const [draft, setDraft] = useState<EntityMapFilterState>(initialCommitted);
    const [isActive, setIsActive] = useState<boolean>(initialIsActive);

    const applyFilters = useCallback(() => {
        setCommitted(draft);
        setIsActive(true);
        savePersistedEntityMapFilters({ isActive: true, committed: draft }, scope);
    }, [draft, scope]);

    const resetFilters = useCallback(() => {
        setDraft(DEFAULT_ENTITY_MAP_FILTER_STATE);
        setCommitted(DEFAULT_ENTITY_MAP_FILTER_STATE);
        setIsActive(false);
        clearPersistedEntityMapFilters(scope);
    }, [scope]);

    const discardDraft = useCallback(() => {
        setDraft(isActive ? committed : DEFAULT_ENTITY_MAP_FILTER_STATE);
    }, [isActive, committed]);

    // Applied on back/forward navigation to restore URL-derived filter state.
    const setFromUrl = useCallback(
        (urlState: { isActive: boolean; committed: EntityMapFilterState } | null) => {
            const target = urlState ?? { isActive: false, committed: DEFAULT_ENTITY_MAP_FILTER_STATE };
            setDraft(target.committed);
            setCommitted(target.committed);
            setIsActive(target.isActive);
            if (target.isActive) {
                savePersistedEntityMapFilters({ isActive: true, committed: target.committed }, scope);
            } else {
                clearPersistedEntityMapFilters(scope);
            }
        },
        [scope],
    );

    return { committed, draft, setDraft, applyFilters, resetFilters, discardDraft, isActive, setFromUrl };
}
